import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import { useModal } from '../components/useModal';
import { weekdaysReverse } from '../../enums/weekdays';
import { COPENHAGEN_TIME_NOTE, formatCopenhagen, formatCourtTime } from '../../common/copenhagenTime';

/**
 * Seasonal court — one recurring slot at a venue, for a season, and the
 * occurrences it has produced.
 *
 * Its one action, Reserve, attaches this court to a seasonal court purchase. The
 * old page asks for the purchase id with `prompt()`: no validation, no way to
 * tell what you are attaching it to, and a typo produces a reservation against
 * whatever that id happens to be. It is a dialog now, the id has to look like a
 * UUID before the button works, and the text says what the reservation does.
 */

// Slot times arrive in UTC; shown in Copenhagen time with the zone (see copenhagenTime.js).
const fmtDate = value => formatCopenhagen(value, { fallback: '—' });
const fmtDay = value => formatCopenhagen(value, { dateOnly: true, fallback: '—' });
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SeasonalCourtInternal = ({ api }) => {
  const { seasonalCourtId } = useParams();
  const { ask, modalProps } = useModal();

  const [court, setCourt] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.seasonalcourts.getCourtWithDetails(seasonalCourtId);
      if (!data || data.success === false || !data.Id) {
        setFailed(true);
        return;
      }
      setCourt(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, seasonalCourtId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/seasonal-courts">← All seasonal courts</BackToList>
        <div className="next-note next-note--error">
          Could not load this seasonal court.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!court) return <div className="next-page next-muted">Loading…</div>;

  const slots = Array.isArray(court.Slots) ? court.Slots : [];

  const reserve = () => ask({
    kind: 'input',
    title: 'Reserve this court from a purchase',
    context: `Reserve · ${court.CourtName || seasonalCourtId}`,
    body: 'The buyer of the membership purchase you name gets this court: its occurrences are reserved for them for the season.',
    label: 'Purchase id (the membership purchase)',
    placeholder: '00000000-0000-0000-0000-000000000000',
    hint: 'The payments purchase (Purchases page), not a seasonal court purchase id. It must be Paid and not hold another court.',
    mono: true,
    confirmLabel: 'Reserve',
    onConfirm: async value => {
      const purchaseId = value.trim();
      if (!GUID.test(purchaseId)) {
        setNote({ tone: 'error', text: 'That is not a UUID — nothing was reserved.' });
        throw new Error('invalid id');
      }
      try {
        await api.seasonalcourts.reserveFromPurchase(court.Id, purchaseId);
        setNote({ tone: 'info', text: 'Reserved.' });
      } catch (error) {
        setNote({
          tone: 'error',
          text: `Unable to reserve: ${error?.payload?.translatedMessage || error?.message || 'unknown error'}`
        });
        throw error;
      }
      await load();
    }
  });

  const facts = [
    ['Court name', court.CourtName || <span className="next-muted">—</span>],
    ['Status', court.Status
      ? <StatusPill status={court.Status}>{court.Status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Week day', weekdaysReverse[court.WeekDay] || <span className="next-muted">—</span>],
    ['Time (Copenhagen)', court.Time ? formatCourtTime(court.Time) : <span className="next-muted">—</span>],
    ['Season', `${fmtDay(court.SeasonStart)} → ${fmtDay(court.SeasonEnd)}`],
    ['Facility id', court.FacilityId
      ? <Guid value={court.FacilityId} to={`/next/facility/${court.FacilityId}`} />
      : <span className="next-muted">—</span>],
    ['Membership id', court.MembershipId
      ? <Guid value={court.MembershipId} href={`/product/details/${court.MembershipId}`} />
      : <span className="next-muted">—</span>],
    ['Product id', court.ProductId
      ? <Guid value={court.ProductId} href={`/product/details/${court.ProductId}`} />
      : <span className="next-muted">—</span>],
    ['Created at', fmtDate(court.CreatedAt)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/seasonal-courts">← All seasonal courts</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{court.CourtName || 'Seasonal court'}</h1>
          {court.Status && <StatusPill status={court.Status}>{court.Status}</StatusPill>}
        </div>
        <span className="next-detail__id">{court.Id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Court</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Slots</h2>
              <span className="next-muted">
                {slots.length} occurrence{slots.length === 1 ? '' : 's'} this season · {COPENHAGEN_TIME_NOTE}
              </span>
            </div>

            {slots.length === 0 ? (
              <p className="next-table__note next-muted">No slot has been produced yet.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Slot</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map(slot => (
                      <tr key={slot.Id}>
                        <td data-label="">
                          <Guid value={slot.Id} to={`/next/seasonal-court-slot/${slot.Id}`} />
                        </td>
                        <td data-label="Start" className="next-nowrap">{fmtDate(slot.Start)}</td>
                        <td data-label="End" className="next-nowrap">{fmtDate(slot.End)}</td>
                        <td data-label="Status">
                          {slot.Status
                            ? <StatusPill status={slot.Status}>{slot.Status}</StatusPill>
                            : <span className="next-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button type="button" className="next-actionlist__item" onClick={reserve}>
                Reserve from a purchase
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                {
                  label: 'Purchases',
                  hint: 'of this court',
                  to: `/next/seasonal-court-purchases?seasonalCourtId=${court.Id}`
                },
                court.FacilityId && { label: 'Facility', to: `/next/facility/${court.FacilityId}` },
                court.FacilityId && {
                  label: 'Courts here',
                  hint: 'same facility',
                  to: `/next/seasonal-courts?FacilityId=${court.FacilityId}`
                },
                { label: 'Availability overview', to: '/next/seasonal-courts-overview' }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

SeasonalCourtInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const SeasonalCourt = withApi(SeasonalCourtInternal);

SeasonalCourt.requiresLogin = true;

export default SeasonalCourt;
